import React, { useContext } from "react";
import {
  IReservationsListProps,
  ICRMReservationProps,
} from "./ReservationsListProps";

import map from "lodash/map";
import filter from "lodash/filter";
import forOwn from "lodash/forOwn";
import { parseDate, resParseDateFormat } from "../../../services/dates";
import { convertArrayToObject } from "../../../services/helpers";
import { useHotels } from "../../../hooks/useHotels";
import ReservationCard from "../../common/ReservationCard/ReservationCard";
import ReservationCardSkeleton from "../../common/ReservationCard/ReservationCardSkeleton";
import { Container } from "./ReservationsListHelpers";
import { useAppSelector } from "../../../hooks";
import Accordion from "react-bootstrap/Accordion";
import AccordionContext from "react-bootstrap/AccordionContext";
import { Col, Row, useAccordionToggle } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { slugify } from "transliteration";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const AddHotelDetailsToRes = (
  bookings: ICRMReservationProps,
  hotels: { [x: string]: any }
) => {
  const _bookings: any = {};
  forOwn(bookings, (reservation: any, key) => {
    _bookings[key] = {
      ...reservation,
      hotel_details: hotels[reservation.pms_property_code],
    };
  });
  return _bookings;
};

const ReservationsList = (props: IReservationsListProps) => {
  const hotelsData = useHotels();

  let reservations: ICRMReservationProps = useAppSelector(
    (state: {
      member: {
        upcomingReservations: any;
        recentStays: any;
        cancelledReservations: any;
      };
    }) => {
      switch (props.type) {
        case "upcoming":
          return state.member.upcomingReservations;
        case "recent":
          return state.member.recentStays;
        case "cancelled":
          return state.member.cancelledReservations;
        default:
          return {};
      }
    }
  );

  //get hotel code of all reservations
  const hotelIds = map(reservations, "pms_property_code");
  const uniqueHotelIds = hotelIds.filter((hId) => hId !== null);

  const reservedHotels = filter(hotelsData, (h) => {
    return h.crs_code && uniqueHotelIds.indexOf(h.crs_code) !== -1;
  });
  const hotelsByCode = convertArrayToObject(reservedHotels, "crs_code");

  // assign hotel details to each reservation
  reservations = AddHotelDetailsToRes(reservations, hotelsByCode);

  const getDateArray = (date: Date) => {
    const day = date.toLocaleString("en-us", { day: "numeric" });
    const month = date.toLocaleString("en-us", { month: "long" });
    const year = date.toLocaleString("en-us", { year: "numeric" });

    return [day, month, year];
  };

  const datesAccordionHeader = (reservation: any) => {
    const arrival = new Date(reservation.arrival);
    const departure = new Date(reservation.departure);

    const arrivalStr = getDateArray(arrival);
    const departureStr = getDateArray(departure);
    const finalDateString =
      arrivalStr[1] +
      " " +
      arrivalStr[0] +
      ", " +
      arrivalStr[2] +
      " - " +
      departureStr[1] +
      " " +
      departureStr[0] +
      ", " +
      departureStr[2];

    return finalDateString;
  };

  const groupReservationUnderDates = (reservations: any) => {
    const datesObject = {};
    reservations.map((res: any) => {
      const dateString = datesAccordionHeader(res);
      if (!(dateString in datesObject)) {
        datesObject[dateString] = [];
      }
      datesObject[dateString].push(res);
    });
    return datesObject;
  };

  const ContextAwareToggle = ({ children, eventKey, callback }: any) => {
    const currentEventKey = useContext(AccordionContext);

    const decoratedOnClick = useAccordionToggle(eventKey, (event) => {
      callback && callback(eventKey);
      const openedPanel = event.target;
      if (openedPanel) {
        // window.scrollTo(0, 0);
      }
    });

    const isCurrentEventKey = currentEventKey === eventKey;

    return (
      <Card.Header onClick={decoratedOnClick} className="open d-flex">
        <span className="accordionIcon">
          {isCurrentEventKey ? (
            <FontAwesomeIcon icon="chevron-up" />
          ) : (
            <FontAwesomeIcon icon="chevron-down" />
          )}
        </span>
        {children}
      </Card.Header>
    );
  };

  const renderReservations = () => {
    if (reservations !== null) {
      const res: any[] = [];
      forOwn(reservations, (reservation) => {
        res.push(reservation);
      });
      const sortType = props.type !== "upcoming" ? "desc" : "asc";
      res &&
        res.sort((a, b) => {
          if (sortType === "asc") {
            return (
              parseDate(a.arrival, resParseDateFormat) -
              parseDate(b.arrival, resParseDateFormat)
            );
          } else {
            return (
              parseDate(b.arrival, resParseDateFormat) -
              parseDate(a.arrival, resParseDateFormat)
            );
          }
        });

      const finalObject = groupReservationUnderDates(res);

      if (Object.keys(finalObject).length > 0) {
        return Object.keys(finalObject).map((dates, index) => {
          return (
            <div className="accordianStay" key={index}>
              <Accordion
                defaultActiveKey={
                  Object.keys(finalObject).length === 1 ? "0" : ""
                }
              >
                <Card key={0}>
                  <ContextAwareToggle eventKey={index.toString()}>
                    <span className="accordionTitle">{dates}</span>
                  </ContextAwareToggle>
                  <Accordion.Collapse eventKey={index.toString()}>
                    <Card.Body>
                      {finalObject[dates].map((reservation: any) => {
                        return (
                          <ReservationCard
                            key={reservation.crs_reservation_id}
                            reservation={reservation}
                            type={props.type}
                            onCancel={props.onCancel}
                          />
                        );
                      })}
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>
              </Accordion>
            </div>
          );
        });
      } else if (props.loading) {
        return (
          <div className="accordianStay">
            <Accordion>
              <Card>
                <ContextAwareToggle eventKey={"0"}>
                  <Row style={{ width: "70%" }}>
                    <Col>
                      <Skeleton height={20} />
                    </Col>
                    <Col>
                      <Skeleton height={20} />
                    </Col>
                  </Row>
                </ContextAwareToggle>
                <Accordion.Collapse eventKey={"0"}>
                  <Card.Body className="p-0">
                    <ReservationCardSkeleton />
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
          </div>
        );
      }
    }
    return;
  };

  const renderHeading = () => {
    let totalResStr = "";
    const totalRes = Object.keys(reservations).length || 0;
    const pluralS = totalRes > 1 ? "s" : "";
    switch (props.type) {
      case "upcoming":
        totalResStr = `Upcoming Reservation${pluralS}`;
        break;
      case "recent":
        totalResStr = `Recent Stay${pluralS}`;
        break;
      case "cancelled":
        totalResStr = `Cancelled Reservation${pluralS}`;
        break;
      default:
        break;
    }
    if (totalRes > 0) {
      totalResStr = `${totalRes} ${totalResStr}`;
      return (
        // <h4>{totalResStr}</h4>
        null
      );
    } else if (!props.loading) {
      return (
        <h4 className="text-center" id={`no-${slugify(totalResStr)}`}>
          No {totalResStr}s
        </h4>
      );
    }
  };

  return (
    <Container>
      {renderHeading()}
      {renderReservations()}
    </Container>
  );
};
export default React.memo(ReservationsList);
